import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { IAdvisor, IMeeting, IProject, SchedulerType } from "../../../types";
import { getAllMeetings, getAllMeetingsByTypes, refreshMeeting, updateMeetingStatus } from "../services/gutcheck-admin-api.service";
import { Box, Table, TableRow, TableCell, Paper, TableContainer, TableHead, TableBody, Rating, Typography, Stack, MenuItem, Autocomplete, TableSortLabel, Button, Tooltip } from "@mui/material";
import { TextField } from "@mui/material";
import { DateDisplay } from "../../meeting/components/DateDisplay";
import { CompanyLogo } from "../../company/components/CompanyLogo";
import { AdvisorProfileSummary } from "../../advisor/components/AdvisorProfileSummary";
import dayjs from "dayjs";
import { AdvisorTable } from "../../company/styles/AdvisorStyles";
import { RefreshCw, CalendarCogIcon, UserCog } from "lucide-react";
import { MeetingModal } from "../meetings/components/MeetingModal";
import { AdvisorModal } from "../advisors/AdvisorModal";

const OnboardingTable = ({ meetings, setMeetings, onEditMeeting, fetchMeetings, onViewAdvisor, defaultSortField = 'event_start', defaultSortDirection = 'desc' }: { 
    meetings: IMeeting[], 
    setMeetings: (meetings: IMeeting[]) => void,
    onEditMeeting: (meeting: IMeeting) => void,
    fetchMeetings: () => Promise<void>,
    onViewAdvisor: (advisor: IAdvisor) => void,
    defaultSortField?: string,
    defaultSortDirection?: 'asc' | 'desc'
}) => {
    const [sortField, setSortField] = useState<string>(defaultSortField || 'event_start');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection || 'desc');
    const [refreshingMeetingId, setRefreshingMeetingId] = useState<string | null>(null);
    const { getAccessTokenSilently } = useAuth0();

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleRefreshMeeting = async (meetingUuid: string) => {
        setRefreshingMeetingId(meetingUuid);
        const accessToken = await getAccessTokenSilently();
        const { data } = await refreshMeeting(meetingUuid, accessToken);
        if (data && data.meeting) {
            fetchMeetings();
        }
        setRefreshingMeetingId(null);
    }

    const sortedMeetings = [...meetings].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
            case 'event_start':
                aValue = a.event_start ? new Date(a.event_start).getTime() : 0;
                bValue = b.event_start ? new Date(b.event_start).getTime() : 0;
                break;
            case 'company':
                aValue = a.project?.name || '';
                bValue = b.project?.name || '';
                break;
            case 'advisor':
                aValue = a.advisor?.name || '';
                bValue = b.advisor?.name || '';
                break;
            case 'company_rating':
                const aCompanyRating = a.ratings?.find((rating: any) => rating.user);
                const bCompanyRating = b.ratings?.find((rating: any) => rating.user);
                aValue = aCompanyRating?.rating || 0;
                bValue = bCompanyRating?.rating || 0;
                break;
            case 'advisor_rating':
                const aAdvisorRating = a.ratings?.filter((rating: any) => rating.advisor)?.sort((x: any, y: any) => x.rating - y.rating)[0];
                const bAdvisorRating = b.ratings?.filter((rating: any) => rating.advisor)?.sort((x: any, y: any) => x.rating - y.rating)[0];
                aValue = aAdvisorRating?.rating || 0;
                bValue = bAdvisorRating?.rating || 0;
                break;
            case 'calendly_host_name':
                aValue = a.calendly_host_name || '';
                bValue = b.calendly_host_name || '';
                break;
            default:
                aValue = a[sortField as keyof IMeeting] || '';
                bValue = b[sortField as keyof IMeeting] || '';
        }

        if (sortDirection === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    return (
        <TableContainer sx={{ marginBottom: "60px", marginTop: "20px" }}>
            <AdvisorTable sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'calendly_host_name'}
                                direction={sortField === 'calendly_host_name' ? sortDirection : 'asc'}
                                onClick={() => handleSort('calendly_host_name')}
                            >
                                Host
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'event_start'}
                                direction={sortField === 'event_start' ? sortDirection : 'asc'}
                                onClick={() => handleSort('event_start')}
                            >
                                Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'company'}
                                direction={sortField === 'company' ? sortDirection : 'asc'}
                                onClick={() => handleSort('company')}
                            >
                                Company
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'advisor'}
                                direction={sortField === 'advisor' ? sortDirection : 'asc'}
                                onClick={() => handleSort('advisor')}
                            >
                                Advisor
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            Campaign
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'company_rating'}
                                direction={sortField === 'company_rating' ? sortDirection : 'asc'}
                                onClick={() => handleSort('company_rating')}
                            >
                                Survey Progress
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'advisor_rating'}
                                direction={sortField === 'advisor_rating' ? sortDirection : 'asc'}
                                onClick={() => handleSort('advisor_rating')}
                            >
                                Survey URL
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                {sortedMeetings.length > 0 ? (
                    <TableBody>
                        {sortedMeetings.map((meeting) => {
                            return (
                                <TableRow key={meeting.id}>
                                    <TableCell>{meeting.calendly_host_name}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            {meeting.event_start ? <DateDisplay meeting={meeting} /> : ""}
                                            <Typography variant="caption">{meeting.event_start ? dayjs(meeting.event_start).format("h:mm a") : ""}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell><CompanyLogo project={meeting.project} style="left" /></TableCell>
                                    <TableCell>
                                        <AdvisorProfileSummary advisor={meeting.advisor} />
                                    </TableCell>
                                    <TableCell>
                                        {meeting.advisor?.campaign_leads?.[0]?.name}
                                    </TableCell>
                                    <TableCell>
                                        {meeting.advisor?.progress}
                                    </TableCell>
                                    <TableCell>
                                        { meeting.advisor?.latest_survey_url}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Tooltip title="Advisor" placement="top">
                                                <Box sx={{ cursor: "pointer" }} onClick={() => onViewAdvisor(meeting.advisor)}>
                                                    <Tooltip title="Advisor" placement="top">
                                                        <UserCog size={16} />
                                                    </Tooltip>
                                                </Box>
                                            </Tooltip>

                                            <Tooltip title="Edit" placement="top">
                                                <Box sx={{ cursor: "pointer"}} onClick={() => onEditMeeting(meeting)} >
                                                    <CalendarCogIcon size={16} />
                                                </Box>
                                            </Tooltip>
                                            <Tooltip title="Refresh" placement="top">
                                                <Box 
                                                    sx={{ 
                                                        cursor: "pointer",
                                                        '& svg': {
                                                            transition: 'transform 1s ease-in-out',
                                                            transform: refreshingMeetingId === meeting.uuid ? 'rotate(360deg)' : 'none',
                                                            color: refreshingMeetingId === meeting.uuid ? 'gray' : 'inherit'
                                                        }
                                                    }} 
                                                    onClick={() => handleRefreshMeeting(meeting.uuid)} 
                                                >
                                                    <RefreshCw size={16} />
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                ) : (
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ backgroundColor: "#f8f9fa" }}>
                                <Box sx={{ display: "inline-block", backgroundColor: "#ffffff", padding: "20px", borderRadius: "5px", margin: "20px", border: "1px solid #c9c9c9" }}>No meetings found</Box>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                )}
            </AdvisorTable>
        </TableContainer>
    )
}
export const AdminOnboarding = () => {

    const [meetings, setMeetings] = useState<IMeeting[]>([]);
    const { getAccessTokenSilently } = useAuth0();
    const [company, setCompany] = useState<string>("");
    const [projects, setProjects] = useState<IProject[]>([]);
    const [filteredMeetings, setFilteredMeetings] = useState<IMeeting[]>([]);
    const [editMode, setEditMode] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState<IMeeting | undefined>(undefined);
    const [advisors, setAdvisors] = useState<IAdvisor[]>([]);
    const [advisorModal, setAdvisorModal] = useState(false);
    const [advisor, setAdvisor] = useState<IAdvisor | null>(null);

    const fetchMeetings = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data } = await getAllMeetingsByTypes(accessToken, "advisor", [SchedulerType.ONBOARDING]);
        if (data.meetings.length > 0) {
            const sortedMeetings = data.meetings.sort((a: any, b: any) => new Date(b.event_start).getTime() - new Date(a.event_start).getTime());
            setMeetings(sortedMeetings);
            setProjects(sortedMeetings.map((meeting: any) => meeting.project).filter((project: IProject, index: number, self: IProject[]) => self.findIndex(p => p.id === project.id) === index).sort((a: IProject, b: IProject) => a.name.localeCompare(b.name)));
            setFilteredMeetings(sortedMeetings);
        }
    }
    useEffect(() => {
        fetchMeetings();
    }, []);

    const handleCompanyChange = (value: string) => {
        setCompany(value);
        if (value === "") {
            setFilteredMeetings(meetings);
        } else {
            setFilteredMeetings(meetings.filter((meeting: IMeeting) => meeting.project.id === parseInt(value)));
        }
    };

    const handleEditMeeting = (meeting: IMeeting) => {
        setSelectedMeeting(meeting);
        setEditMode(true);
    };

    const handleViewAdvisor = (advisor: IAdvisor) => {
        setAdvisor(advisor);
        setAdvisorModal(true);
    }

    const handleAdvisorUpdate = (updatedAdvisor: IAdvisor) => {
        // Update the advisor data in all meetings that reference this advisor
        const updatedMeetings = meetings.map((meeting: IMeeting) => {
            if (meeting.advisor?.id === updatedAdvisor.id) {
                return {
                    ...meeting,
                    advisor: updatedAdvisor
                };
            }
            return meeting;
        });
        
        // Update the meetings state to reflect the advisor changes
        setMeetings(updatedMeetings);
        setFilteredMeetings(updatedMeetings);
    }

    return (
        <div>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Advisor Onboarding Sessions</h3>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'space-between', marginTop: "20px" }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Autocomplete
                            options={projects}
                            getOptionLabel={(option: IProject) => option.name}
                            value={projects.find((project: IProject) => project.id === parseInt(company)) || null}
                            onChange={(event, newValue) => {
                                handleCompanyChange(newValue ? newValue.id.toString() : "");
                            }}
                            size="small"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Company Name"
                                    sx={{ minWidth: 200 }}
                                />
                            )}
                        />
                    </Box>
                </Box>
            </Box>
            <MeetingModal
                meetingUuid={selectedMeeting?.uuid ?? ""}
                reloadMeetings={fetchMeetings}
                editMode={editMode}
                setEditMode={setEditMode} 
            />
            <h5>Upcoming Onboarding Sessions</h5>
            <OnboardingTable 
                meetings={filteredMeetings.filter((meeting: IMeeting) => dayjs(meeting.event_start).isAfter(dayjs()))} 
                setMeetings={setMeetings}
                onEditMeeting={handleEditMeeting}
                fetchMeetings={fetchMeetings}
                onViewAdvisor={handleViewAdvisor}
                defaultSortField="event_start"
                defaultSortDirection="asc"
            />
            <h5>Upcoming Onboarding Sessions</h5>
            <OnboardingTable 
                meetings={filteredMeetings.filter((meeting: IMeeting) => dayjs(meeting.event_start).isBefore(dayjs()))} 
                setMeetings={setMeetings}
                onEditMeeting={handleEditMeeting}
                fetchMeetings={fetchMeetings}
                onViewAdvisor={handleViewAdvisor}
                defaultSortField="event_start"
                defaultSortDirection="desc"
            />
            {advisorModal && advisor && <AdvisorModal open={advisorModal} onClose={() => setAdvisorModal(false)} advisor={advisor} initialTab="onboarding" onAdvisorUpdate={handleAdvisorUpdate} />}
        </div>
    );
};